<script setup>
/**
 * Removing a piece of wording from WhatsApp. IRREVERSIBLE, AT SOMEBODY ELSE'S SYSTEM.
 *
 * ── WHY THIS IS A DIALOG WITH A TYPING TASK IN IT ───────────────────────────────────
 *
 * Four separate facts make this the most dangerous control in the section, and an owner knows
 * none of them:
 *
 *   1. It cannot be undone. Not by us, not by Meta, not by support.
 *   2. META DELETES BY NAME, NOT BY TEMPLATE. A request carrying only the name removes EVERY
 *      LANGUAGE of it — the Arabic and the English wording, both gone, from one click. A business
 *      with `order_ready_v1` in two languages is exactly the business this ruins.
 *   3. An approved template's NAME is then unusable for 30 days. She cannot simply re-create it.
 *   4. Anything already built on it — a broadcast, her own systems calling the outbound API —
 *      stops working, and the failure arrives per recipient at send time.
 *
 * So: a scope the owner CHOOSES rather than one we pick for her, every consequence written out
 * where she decides, and the template's own name typed by hand before the button is live. The
 * typing is not ceremony — it is the only mechanism that distinguishes "I meant this template"
 * from "I clicked the wrong row", and the rows on this screen look alike by design.
 *
 * The confirm button is ruby and says what it does. It never says "OK".
 */
import { computed, ref } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** The row being removed, or null while the dialog has never been opened. */
  template: { type: Object, default: null },
  /**
   * The OTHER languages this name exists in. Empty means the name has exactly one template, so
   * the two scopes are the same act and there is nothing to choose — offering the choice then
   * would be a question with one answer, which teaches an owner to stop reading the dialog.
   */
  otherLanguages: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
  /** Meta's own sentence when Meta refused the removal. */
  failure: { type: String, default: '' },
});

const emit = defineEmits(['confirm']);

const { t } = useWasloI18n();

const dialogRef = ref(null);
const typed = ref('');
const scope = ref('language');

const scopeOptions = computed(() => [
  {
    value: 'language',
    label: t('WASLO.TEMPLATES.REMOVE.SCOPE_LANGUAGE', {
      language: props.template?.language ?? '',
    }),
  },
  {
    value: 'name',
    label: t('WASLO.TEMPLATES.REMOVE.SCOPE_NAME', {
      count: props.otherLanguages.length + 1,
    }),
  },
]);

/** Exactly, and untrimmed on the left so a stray leading space is a mismatch and not a pass. */
const confirmed = computed(
  () =>
    typed.value.trim() === (props.template?.name ?? '') && typed.value !== ''
);

const open = () => {
  typed.value = '';
  scope.value = 'language';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const confirm = () => {
  if (!confirmed.value || props.busy) return;
  emit('confirm', { scope: scope.value });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="lg"
    :title="t('WASLO.TEMPLATES.REMOVE.TITLE')"
    :confirm-button-label="
      busy
        ? t('WASLO.TEMPLATES.REMOVE.REMOVING')
        : t('WASLO.TEMPLATES.REMOVE.CONFIRM')
    "
    :cancel-button-label="t('WASLO.TEMPLATES.REMOVE.CANCEL')"
    :disable-confirm-button="!confirmed || busy"
    :is-loading="busy"
    @confirm="confirm"
  >
    <div class="flex flex-col gap-4" data-waslo-template-remove-dialog>
      <p class="mb-0 text-sm leading-6 text-n-slate-11">
        {{ t('WASLO.TEMPLATES.REMOVE.BODY') }}
      </p>

      <ul class="flex flex-col gap-1.5 ps-0 mb-0 list-none">
        <li
          v-for="key in ['FOREVER', 'NAME_LOCKED', 'IN_FLIGHT', 'BREAKS_SENDS']"
          :key="key"
          class="flex items-start gap-2 text-sm leading-6 text-n-slate-11"
        >
          <span
            class="shrink-0 mt-1.5 size-1.5 rounded-full bg-n-ruby-9"
            aria-hidden="true"
          />
          <span>{{ t(`WASLO.TEMPLATES.REMOVE.${key}`) }}</span>
        </li>
      </ul>

      <!--
        Only when there is a real choice. Meta's name-only delete takes every language with it,
        and that is the single most surprising thing on this screen.
      -->
      <WasloSelect
        v-if="otherLanguages.length"
        v-model="scope"
        :label="t('WASLO.TEMPLATES.REMOVE.SCOPE_LABEL')"
        :options="scopeOptions"
        :message="
          scope === 'name'
            ? t('WASLO.TEMPLATES.REMOVE.SCOPE_NAME_WARNING', {
                languages: otherLanguages.join(', '),
              })
            : ''
        "
        :message-type="scope === 'name' ? 'error' : 'info'"
        data-waslo-template-remove-scope
      />

      <WasloInput
        v-model="typed"
        :label="
          t('WASLO.TEMPLATES.REMOVE.TYPE_LABEL', {
            name: template?.name ?? '',
          })
        "
        :hint="t('WASLO.TEMPLATES.REMOVE.TYPE_HINT')"
        :disabled="busy"
        data-waslo-template-remove-typed
      />

      <p
        v-if="failure"
        class="mb-0 text-sm leading-6 text-n-ruby-9"
        dir="auto"
        data-waslo-template-remove-error
      >
        <bdi>{{ failure }}</bdi>
      </p>
    </div>
  </Dialog>
</template>
